import { useCallback, memo } from 'react';
import { Wind, ZoomIn, ZoomOut, ArrowRight, ArrowLeft, ArrowUp, ArrowDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PropertySection } from '../components';
import type { TimelineItem } from '@/types/timeline';
import type { CanvasSettings } from '@/types/transform';
import { resolveTransform, getSourceDimensions } from '@/lib/composition-runtime/utils/transform-resolver';
import { removeKeyframesForProperty, addKeyframes } from '@/features/timeline/stores/actions/keyframe-actions';
import { useTranslation } from '@/features/i18n/locales';

interface AnimationSectionProps {
    items: TimelineItem[];
    canvas: CanvasSettings;
}

export const AnimationSection = memo(function AnimationSection({
    items,
    canvas,
}: AnimationSectionProps) {
    const t = useTranslation();
    const applyAnimation = useCallback((type: 'zoom-in' | 'zoom-out' | 'pan-right' | 'pan-left' | 'pan-up' | 'pan-down') => {
        items.forEach((item) => {
            // Only for visual items
            if (item.type === 'audio' || item.type === 'adjustment') return;

            const duration = item.durationInFrames;
            if (duration <= 1) return;

            // Get base resolved transform
            const sourceDimensions = getSourceDimensions(item);
            const base = resolveTransform(item, canvas, sourceDimensions);

            // Clear existing keyframes for transform properties
            ['x', 'y', 'width', 'height', 'scale'].forEach((prop) => {
                removeKeyframesForProperty(item.id, prop as any);
            });

            const payloads: any[] = [];
            const ZOOM_FACTOR = 1.25;

            switch (type) {
                case 'zoom-in':
                    // Zoom IN the content (bounding box stays fixed)
                    payloads.push({ itemId: item.id, property: 'scale', frame: 0, value: 1.0, easing: 'ease-in-out' });
                    payloads.push({ itemId: item.id, property: 'scale', frame: duration - 1, value: ZOOM_FACTOR, easing: 'ease-in-out' });
                    break;
                case 'zoom-out':
                    // Zoom OUT the content (bounding box stays fixed)
                    payloads.push({ itemId: item.id, property: 'scale', frame: 0, value: ZOOM_FACTOR, easing: 'ease-in-out' });
                    payloads.push({ itemId: item.id, property: 'scale', frame: duration - 1, value: 1.0, easing: 'ease-in-out' });
                    break;
                case 'pan-right':
                    // Slightly zoomed (1.2x) to allow panning without black edges
                    const zoomedRW = base.width * 1.2;
                    const zoomedRH = base.height * 1.2;
                    const panX = base.width * 0.1;
                    payloads.push({ itemId: item.id, property: 'width', frame: 0, value: zoomedRW, easing: 'ease-in-out' });
                    payloads.push({ itemId: item.id, property: 'height', frame: 0, value: zoomedRH, easing: 'ease-in-out' });
                    payloads.push({ itemId: item.id, property: 'x', frame: 0, value: base.x - panX, easing: 'ease-in-out' });
                    payloads.push({ itemId: item.id, property: 'x', frame: duration - 1, value: base.x + panX, easing: 'ease-in-out' });
                    break;
                case 'pan-left':
                    const zoomedLW = base.width * 1.2;
                    const zoomedLH = base.height * 1.2;
                    const pLX = base.width * 0.1;
                    payloads.push({ itemId: item.id, property: 'width', frame: 0, value: zoomedLW, easing: 'ease-in-out' });
                    payloads.push({ itemId: item.id, property: 'height', frame: 0, value: zoomedLH, easing: 'ease-in-out' });
                    payloads.push({ itemId: item.id, property: 'x', frame: 0, value: base.x + pLX, easing: 'ease-in-out' });
                    payloads.push({ itemId: item.id, property: 'x', frame: duration - 1, value: base.x - pLX, easing: 'ease-in-out' });
                    break;
                case 'pan-up':
                    const zoomedUW = base.width * 1.2;
                    const zoomedUH = base.height * 1.2;
                    const panY = base.height * 0.1;
                    payloads.push({ itemId: item.id, property: 'width', frame: 0, value: zoomedUW, easing: 'ease-in-out' });
                    payloads.push({ itemId: item.id, property: 'height', frame: 0, value: zoomedUH, easing: 'ease-in-out' });
                    payloads.push({ itemId: item.id, property: 'y', frame: 0, value: base.y + panY, easing: 'ease-in-out' });
                    payloads.push({ itemId: item.id, property: 'y', frame: duration - 1, value: base.y - panY, easing: 'ease-in-out' });
                    break;
                case 'pan-down':
                    const zoomedDW = base.width * 1.2;
                    const zoomedDH = base.height * 1.2;
                    const pDY = base.height * 0.1;
                    payloads.push({ itemId: item.id, property: 'width', frame: 0, value: zoomedDW, easing: 'ease-in-out' });
                    payloads.push({ itemId: item.id, property: 'height', frame: 0, value: zoomedDH, easing: 'ease-in-out' });
                    payloads.push({ itemId: item.id, property: 'y', frame: 0, value: base.y - pDY, easing: 'ease-in-out' });
                    payloads.push({ itemId: item.id, property: 'y', frame: duration - 1, value: base.y + pDY, easing: 'ease-in-out' });
                    break;
            }

            if (payloads.length > 0) {
                addKeyframes(payloads);
            }
        });
    }, [items, canvas]);

    const clearAnimations = useCallback(() => {
        items.forEach((item) => {
            ['x', 'y', 'width', 'height', 'rotation', 'opacity'].forEach((prop) => {
                removeKeyframesForProperty(item.id, prop as any);
            });
        });
    }, [items]);

    return (
        <PropertySection title={t.properties.animationPresets} icon={Wind} defaultOpen={false}>
            <div className="grid grid-cols-2 gap-2 mt-2">
                <Button
                    variant="secondary"
                    size="sm"
                    className="h-8 text-[10px] gap-1.5"
                    onClick={() => applyAnimation('zoom-in')}
                >
                    <ZoomIn className="w-3 h-3" />
                    {t.properties.zoomIn}
                </Button>
                <Button
                    variant="secondary"
                    size="sm"
                    className="h-8 text-[10px] gap-1.5"
                    onClick={() => applyAnimation('zoom-out')}
                >
                    <ZoomOut className="w-3 h-3" />
                    {t.properties.zoomOut}
                </Button>
                <Button
                    variant="secondary"
                    size="sm"
                    className="h-8 text-[10px] gap-1.5"
                    onClick={() => applyAnimation('pan-right')}
                >
                    <ArrowRight className="w-3 h-3" />
                    {t.properties.panRight}
                </Button>
                <Button
                    variant="secondary"
                    size="sm"
                    className="h-8 text-[10px] gap-1.5"
                    onClick={() => applyAnimation('pan-left')}
                >
                    <ArrowLeft className="w-3 h-3" />
                    {t.properties.panLeft}
                </Button>
                <Button
                    variant="secondary"
                    size="sm"
                    className="h-8 text-[10px] gap-1.5"
                    onClick={() => applyAnimation('pan-up')}
                >
                    <ArrowUp className="w-3 h-3" />
                    {t.properties.panUp}
                </Button>
                <Button
                    variant="secondary"
                    size="sm"
                    className="h-8 text-[10px] gap-1.5"
                    onClick={() => applyAnimation('pan-down')}
                >
                    <ArrowDown className="w-3 h-3" />
                    {t.properties.panDown}
                </Button>
            </div>
            <Button
                variant="ghost"
                size="sm"
                className="w-full mt-2 h-7 text-[10px] text-muted-foreground hover:text-destructive"
                onClick={clearAnimations}
            >
                {t.properties.clearAnimations}
            </Button>
        </PropertySection>
    );
});
